(() => {
  const initializedCarousels = new WeakSet();
  const initializedStylings = new WeakSet();
  const initializedDialogs = new WeakSet();
  const initializedInlineVideos = new WeakSet();
  const initializedVideos = new WeakSet();
  const initializedInteractiveMedias = new WeakSet();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const pauseDialogMedia = (dialog) => {
    dialog.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
  };

  const initDialogs = (root = document) => {
    root.querySelectorAll('[data-swift4-modal]').forEach((dialog) => {
      if (initializedDialogs.has(dialog)) return;
      initializedDialogs.add(dialog);

      const close = () => {
        pauseDialogMedia(dialog);
        if (dialog.open) dialog.close();
      };

      dialog.querySelectorAll('[data-swift4-modal-close]').forEach((button) => {
        button.addEventListener('click', close);
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });

      dialog.addEventListener('close', () => pauseDialogMedia(dialog));
    });

    root.querySelectorAll('[data-swift4-modal-open]').forEach((button) => {
      if (button.dataset.swift4ModalReady === 'true') return;
      button.dataset.swift4ModalReady = 'true';

      button.addEventListener('click', () => {
        const selector = button.getAttribute('data-swift4-modal-open');
        const dialog = document.querySelector(selector);
        if (!dialog || typeof dialog.showModal !== 'function') return;

        dialog.showModal();
        const video = dialog.querySelector('video');
        if (video) {
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => {});
        }
      });
    });
  };

  const initCarousel = (carousel) => {
    if (initializedCarousels.has(carousel)) return;
    initializedCarousels.add(carousel);

    const track = carousel.querySelector('[data-swift4-carousel-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-swift4-carousel-slide]'));
    const previous = carousel.querySelector('[data-swift4-carousel-prev]');
    const next = carousel.querySelector('[data-swift4-carousel-next]');
    const progress = carousel.querySelector('[data-swift4-carousel-progress]');
    const tabs = Array.from(carousel.querySelectorAll('[data-swift4-carousel-tab]'));
    const howToSection = carousel.closest('.swift4-how-to');
    const stepLines = howToSection
      ? Array.from(howToSection.querySelectorAll('.swift4-section-header .swift4-copy > p'))
      : [];
    const syncStepLines = stepLines.length > 1 && stepLines.length === slides.length;
    const intervalMs =
      parseInt(
        carousel.getAttribute('data-autoplay-interval') ||
          carousel.closest('[data-autoplay-interval]')?.getAttribute('data-autoplay-interval'),
        10
      ) || 0;

    if (!track || slides.length === 0) return;

    let activeIndex = 0;
    let frame = null;
    let timer = null;
    let isPaused = false;
    let isProgrammaticScroll = false;
    let programmaticScrollTimeout = null;

    const setActive = (index) => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));
      const percentage = slides.length === 1 ? 100 : ((activeIndex + 1) / slides.length) * 100;

      if (progress) progress.style.setProperty('--swift4-progress', `${percentage}%`);
      if (previous) previous.disabled = slides.length <= 1 || activeIndex <= 0;
      if (next) next.disabled = slides.length <= 1 || activeIndex >= slides.length - 1;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        const slideVideo = slide.querySelector('video');
        if (slideVideo) {
          if (isActive) {
            slideVideo.currentTime = 0;
            const playPromise = slideVideo.play();
            if (playPromise) playPromise.catch(() => {});
          } else {
            slideVideo.pause();
          }
        }
      });

      tabs.forEach((tab, tabIndex) => {
        tab.setAttribute('aria-current', tabIndex === activeIndex ? 'true' : 'false');
      });

      if (syncStepLines) {
        stepLines.forEach((line, lineIndex) => {
          const isLineActive = lineIndex === activeIndex;
          line.classList.toggle('is-active', isLineActive);
          line.setAttribute('aria-current', isLineActive ? 'true' : 'false');
        });
      }
    };

    const scrollToSlide = (index) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[targetIndex];
      if (!slide) return;
      const targetScrollLeft =
        track.scrollLeft +
        slide.getBoundingClientRect().left -
        track.getBoundingClientRect().left -
        (track.clientWidth - slide.clientWidth) / 2;

      isProgrammaticScroll = true;
      if (programmaticScrollTimeout) clearTimeout(programmaticScrollTimeout);
      programmaticScrollTimeout = setTimeout(() => {
        isProgrammaticScroll = false;
      }, 600);

      track.scrollTo({
        left: targetScrollLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
      setActive(targetIndex);
    };

    const nextSlide = () => {
      const nextIndex = (activeIndex + 1) % slides.length;
      scrollToSlide(nextIndex);
    };

    const stopAutoplay = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (intervalMs <= 0 || slides.length <= 1) return;
      timer = setInterval(() => {
        if (!isPaused) {
          nextSlide();
        }
      }, intervalMs);
    };

    const resetAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    const updateFromScroll = () => {
      frame = null;
      if (isProgrammaticScroll) return;
      const trackBox = track.getBoundingClientRect();
      const trackCenter = trackBox.left + trackBox.width / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const box = slide.getBoundingClientRect();
        const distance = Math.abs(box.left + box.width / 2 - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActive(closestIndex);
    };

    track.addEventListener(
      'scroll',
      () => {
        if (frame !== null) return;
        frame = window.requestAnimationFrame(updateFromScroll);
      },
      { passive: true }
    );

    previous?.addEventListener('click', () => {
      if (activeIndex > 0) {
        scrollToSlide(activeIndex - 1);
        resetAutoplay();
      }
    });
    next?.addEventListener('click', () => {
      if (activeIndex < slides.length - 1) {
        scrollToSlide(activeIndex + 1);
        resetAutoplay();
      }
    });
    tabs.forEach((tab, index) =>
      tab.addEventListener('click', () => {
        scrollToSlide(index);
        resetAutoplay();
      })
    );

    slides.forEach((slide) => {
      const slideVideo = slide.querySelector('video');
      if (slideVideo && !slideVideo.loop) {
        slideVideo.addEventListener('ended', () => {
          nextSlide();
          resetAutoplay();
        });
      }
    });

    if (syncStepLines) {
      stepLines.forEach((line, lineIndex) => {
        line.classList.add('swift4-how-to__step-line');
        line.setAttribute('role', 'button');
        line.setAttribute('tabindex', '0');

        const goToStep = () => {
          scrollToSlide(lineIndex);
          resetAutoplay();
        };

        line.addEventListener('click', goToStep);
        line.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            goToStep();
          }
        });
      });
    }

    carousel.addEventListener('mouseenter', () => {
      isPaused = true;
    });

    carousel.addEventListener('mouseleave', () => {
      isPaused = false;
      resetAutoplay();
    });

    carousel.addEventListener(
      'touchstart',
      () => {
        isProgrammaticScroll = false;
        isPaused = true;
      },
      { passive: true }
    );

    carousel.addEventListener(
      'touchend',
      () => {
        isPaused = false;
        resetAutoplay();
      },
      { passive: true }
    );

    track.addEventListener(
      'wheel',
      () => {
        isProgrammaticScroll = false;
        resetAutoplay();
      },
      { passive: true }
    );

    let isPointerDown = false;
    let pointerStartX = 0;
    let pointerScrollLeft = 0;
    let hasDragged = false;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      isProgrammaticScroll = false;
      isPointerDown = true;
      hasDragged = false;
      pointerStartX = e.clientX;
      pointerScrollLeft = track.scrollLeft;
      isPaused = true;
      resetAutoplay();
    });

    window.addEventListener('pointermove', (e) => {
      if (!isPointerDown) return;
      const deltaX = e.clientX - pointerStartX;
      if (Math.abs(deltaX) > 5) {
        hasDragged = true;
        track.scrollLeft = pointerScrollLeft - deltaX;
      }
    });

    const handlePointerUp = () => {
      if (!isPointerDown) return;
      isPointerDown = false;
      isPaused = false;
      resetAutoplay();
      if (hasDragged) {
        updateFromScroll();
      }
    };

    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !reduceMotion.matches) {
              isPaused = false;
              startAutoplay();
            } else {
              stopAutoplay();
            }
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(carousel);
    } else {
      startAutoplay();
    }

    setActive(0);
  };

  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-swift4-carousel]').forEach(initCarousel);
  };

  const initStyling = (styling) => {
    if (initializedStylings.has(styling)) return;
    initializedStylings.add(styling);

    const mediaItems = Array.from(styling.querySelectorAll('[data-swift4-styling-media]'));
    const infoItems = Array.from(styling.querySelectorAll('[data-swift4-styling-info]'));
    const tabs = Array.from(styling.querySelectorAll('[data-swift4-styling-tab]'));
    const intervalMs = parseInt(styling.getAttribute('data-autoplay-interval'), 10) || 3000;

    if (mediaItems.length === 0 || tabs.length === 0) return;

    let activeIndex = 0;
    let timer = null;
    let isPaused = false;

    const setActive = (index) => {
      activeIndex = (index + tabs.length) % tabs.length;

      mediaItems.forEach((item, i) => {
        const isActive = i === activeIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      infoItems.forEach((item, i) => {
        const isActive = i === activeIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      tabs.forEach((tab, i) => {
        const isActive = i === activeIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    const nextSlide = () => {
      setActive(activeIndex + 1);
    };

    const stopAutoplay = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (tabs.length <= 1) return;
      timer = setInterval(() => {
        if (!isPaused) {
          nextSlide();
        }
      }, intervalMs);
    };

    const resetAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        setActive(index);
        resetAutoplay();
      });
    });

    styling.addEventListener('mouseenter', () => {
      isPaused = true;
    });

    styling.addEventListener('mouseleave', () => {
      isPaused = false;
    });

    let touchStartX = 0;
    let touchEndX = 0;
    styling.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    styling.addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchEndX - touchStartX;
        if (Math.abs(diff) > 40) {
          if (diff < 0) {
            setActive(activeIndex + 1);
          } else {
            setActive(activeIndex - 1);
          }
          resetAutoplay();
        }
      },
      { passive: true }
    );

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !reduceMotion.matches) {
              isPaused = false;
              startAutoplay();
            } else {
              stopAutoplay();
            }
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(styling);
    } else {
      startAutoplay();
    }

    setActive(0);
  };

  const initStylings = (root = document) => {
    root.querySelectorAll('[data-swift4-styling]').forEach(initStyling);
  };

  const initInlineVideos = (root = document) => {
    root.querySelectorAll('[data-swift4-inline-video]').forEach((frame) => {
      if (initializedInlineVideos.has(frame)) return;
      initializedInlineVideos.add(frame);

      const video = frame.querySelector('video');
      const toggle = frame.querySelector('[data-swift4-inline-video-toggle]');
      if (!video) return;
      const label = toggle ? (toggle.getAttribute('aria-label')?.replace(/^Play\s+/, '') || 'video') : 'video';

      const reset = () => {
        frame.removeAttribute('data-playing');
        if (toggle) toggle.setAttribute('aria-label', `Play ${label}`);
      };

      if (toggle) {
        toggle.addEventListener('click', () => {
          const promise = video.play();
          frame.setAttribute('data-playing', 'true');
          toggle.setAttribute('aria-label', `Playing ${label}`);
          if (promise) promise.catch(reset);
        });
      }

      video.addEventListener('click', () => {
        if (!video.controls) {
          if (video.paused) {
            const promise = video.play();
            frame.setAttribute('data-playing', 'true');
            if (toggle) toggle.setAttribute('aria-label', `Playing ${label}`);
            if (promise) promise.catch(reset);
          } else {
            video.pause();
          }
        }
      });

      video.addEventListener('play', () => {
        frame.setAttribute('data-playing', 'true');
      });

      video.addEventListener('ended', reset);
      video.addEventListener('pause', reset);
    });
  };

  const initAutoplayVideos = (root = document) => {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55 && !reduceMotion.matches) {
            const promise = video.play();
            if (promise) promise.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.55, 1] }
    );

    root.querySelectorAll('video[autoplay]').forEach((video) => {
      if (initializedVideos.has(video)) return;
      initializedVideos.add(video);
      observer.observe(video);
    });
  };

  const trackedInteractiveMedias = new Set();
  let interactiveScrollBound = false;

  const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

  const updateInteractiveMedias = () => {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const stickyNavigationHeight = isMobile
      ? document.querySelector('.sticky-navbar-section')?.getBoundingClientRect().height || 0
      : 0;

    trackedInteractiveMedias.forEach((element) => {
      if (!element.isConnected) {
        trackedInteractiveMedias.delete(element);
        return;
      }
      const rect = element.getBoundingClientRect();
      const viewport = element.querySelector('.swift4-interactive-media__viewport');
      const viewportHeight = viewport?.getBoundingClientRect().height || vh;
      const stickyTop = isMobile
        ? Math.max(0, Math.min(stickyNavigationHeight, vh - viewportHeight))
        : 0;

      element.style.setProperty('--swift4-interactive-sticky-top', `${stickyTop.toFixed(1)}px`);
      const scrollDistance = rect.height - viewportHeight;

      // 没有额外定屏距离时，按媒体实际可见比例驱动渐变。
      // 这样移动端在图片进入视口时就开始显示效果，不必等图片滚出一半。
      if (scrollDistance <= 10) {
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, vh);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const maximumVisibleHeight = Math.min(rect.height, vh);
        const visibleRatio = maximumVisibleHeight > 0
          ? clamp(visibleHeight / maximumVisibleHeight, 0, 1)
          : 0;
        const overlayProgress = clamp((visibleRatio - 0.15) / 0.75, 0, 1);
        const textProgress = clamp((visibleRatio - 0.35) / 0.55, 0, 1);
        const overlayOpacity = 0.5 - 0.5 * Math.cos(Math.PI * overlayProgress);
        const textOpacity = 0.5 - 0.5 * Math.cos(Math.PI * textProgress);
        element.style.setProperty('--swift4-interactive-overlay-opacity', overlayOpacity.toFixed(3));
        element.style.setProperty('--swift4-interactive-title-opacity', textOpacity.toFixed(3));
        element.style.setProperty('--swift4-interactive-title-y', `${((1 - textOpacity) * 28).toFixed(1)}px`);
        return;
      }

      // 定屏滚动交互逻辑：
      // 1. 元素进入视口但尚未到达吸顶位置：保持无遮罩、无文字的原图状态
      // 2. 元素到达吸顶位置并处于 sticky 状态（0 <= stickyTop - rect.top <= scrollDistance）：
      //    - 前 22% 滚程 (0.00 ~ 0.22): 定屏留白缓冲期，用户完整看清高清大图，无遮罩与文案
      //    - 中 56% 滚程 (0.22 ~ 0.78): 渐变遮罩平滑加深（0 -> 1），文案平滑上浮并渐现（0 -> 1）
      //    - 后 22% 滚程 (0.78 ~ 1.00): 遮罩与文案完全显现，保持定屏停留供用户阅读
      // 3. 元素滚过底端（stickyTop - rect.top > scrollDistance）：随页面顺畅继续向下滚动
      const scrolled = stickyTop - rect.top;
      const rawProgress = clamp(scrolled / scrollDistance, 0, 1);

      let overlayOpacity = 0;
      let titleOpacity = 0;
      let titleY = 28;

      if (rawProgress <= 0.22) {
        overlayOpacity = 0;
        titleOpacity = 0;
        titleY = 28;
      } else if (rawProgress >= 0.78) {
        overlayOpacity = 1;
        titleOpacity = 1;
        titleY = 0;
      } else {
        const t = (rawProgress - 0.22) / (0.78 - 0.22);
        // 使用正弦平滑曲线
        const ease = 0.5 - 0.5 * Math.cos(Math.PI * t);
        overlayOpacity = ease;

        // 文字稍有层次地跟进
        const textT = clamp((t - 0.05) / 0.95, 0, 1);
        const textEase = 0.5 - 0.5 * Math.cos(Math.PI * textT);
        titleOpacity = textEase;
        titleY = (1 - textEase) * 28;
      }

      element.style.setProperty('--swift4-interactive-overlay-opacity', overlayOpacity.toFixed(3));
      element.style.setProperty('--swift4-interactive-title-opacity', titleOpacity.toFixed(3));
      element.style.setProperty('--swift4-interactive-title-y', `${titleY.toFixed(1)}px`);

      if (rawProgress >= 0.5) {
        element.classList.add('is-active');
      } else {
        element.classList.remove('is-active');
      }
    });
  };

  const initInteractiveMedias = (root = document) => {
    const elements = root.querySelectorAll('[data-swift4-interactive-media]');
    if (!elements.length) return;

    elements.forEach((element) => trackedInteractiveMedias.add(element));

    if (!interactiveScrollBound) {
      interactiveScrollBound = true;
      let ticking = false;
      const onScrollOrResize = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            updateInteractiveMedias();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
    }

    updateInteractiveMedias();
  };

  const init = (root = document) => {
    initDialogs(root);
    initCarousels(root);
    initStylings(root);
    initInlineVideos(root);
    initAutoplayVideos(root);
    initInteractiveMedias(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
